import { useQuery } from '@apollo/client';
import * as R from 'ramda';
import { ApolloProviderV4 } from '~/apollo/client-v4';
import { BookmarkParentType } from '~/apollo/generated/v3/graphql';
import { graphql } from '~/apollo/generated/v4';
import { NotFound } from '~/components/common/NotFound';
import { SpinnerPlaceholder } from '~/components/common/SpinnerPlaceholder';
import { SOItemListV4 } from '~/components/supporting-object/so-item-list-v4';
import { TaggedSOItemListV4 } from '~/components/supporting-object/tagged-so-item-list-v4';
import { useRouteParam } from '~/hooks/routing';
import { createRefetchQueries } from '~/utils/graphql';

const OUTCROP_SEDIMENTARY_LOGS_PAGE = graphql(`
  query OutcropSedimentaryLogsPage($id: ID!) {
    outcropGet(id: $id) {
      id
      sedimentaryLogs {
        ...sedimentaryLogParts
        pictures {
          ...soPictureParts
        }
        files {
          ...fileParts
        }
        georeferences {
          ...georeferenceParts
        }
      }

      studies {
        ...studyParts
        sedimentaryLogs {
          ...sedimentaryLogParts
          pictures {
            ...soPictureParts
          }
          files {
            ...fileParts
          }
          georeferences {
            ...georeferenceParts
          }
        }
      }
    }
  }
`);

export default function OutcropSedimentaryLogsPage() {
  return (
    <ApolloProviderV4>
      <OutcropSedimentaryLogsPageInner />
    </ApolloProviderV4>
  );
}

function OutcropSedimentaryLogsPageInner() {
  const outcropId = useRouteParam('outcropId', parseInt);

  const { data, loading } = useQuery(OUTCROP_SEDIMENTARY_LOGS_PAGE, {
    variables: { id: outcropId },
  });

  const refetchQueries = createRefetchQueries([
    { query: OUTCROP_SEDIMENTARY_LOGS_PAGE, variables: { id: outcropId } },
  ]);

  const outcrop = data?.outcropGet;
  const sedLogs = outcrop?.sedimentaryLogs ?? [];
  const outcropStudies =
    outcrop?.studies.slice().sort(R.ascend(s => s.name)) ?? [];

  if (loading) return <SpinnerPlaceholder />;
  if (!outcrop) return <NotFound />;

  return (
    <>
      <SOItemListV4
        items={sedLogs}
        soParentType="outcrop"
        soParentId={outcropId}
        bookmarkParentType={BookmarkParentType.SedimentaryLog}
        fade
        refetchQueries={refetchQueries}
      />

      <TaggedSOItemListV4
        outcropId={outcropId}
        studies={outcropStudies}
        supportObjectKey="sedimentaryLogs"
        fade
        refetchQueries={refetchQueries}
      />
    </>
  );
}
